import { FC, ReactNode, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { Events } from '../../constants';
import { RootState } from '../../store';
import { addUserNotification, removeUserNotification } from '../../store/auth';
import AnnouncementsModal from '../Announcements/AnnouncementsModal';
import useWhatsNewAccess from '../../utils/hooks/useWhatsNewAccess';

import { Theme } from '../../theme';
import { useAuth, useFetchSettings, useSocket } from '../../utils';
import { Alert, GlobalStyle, Loading } from '../ui';
import ErrorComponent from '../ui/Status/error';

interface AppProps {
  children: ReactNode;
  setTheme: (theme?: Theme) => void;
}
const devErrorMessage =
  'Sorry, something went wrong. Please open the browser console to view the detailed error message.';
// const prodErrorMessage = "Sorry, something went wrong. We're working on getting this fixed as soon as we can.";
const App: FC<AppProps> = ({ children, setTheme }) => {
  const notification = useSelector((state: RootState) => state.notification);
  const authUser = useSelector((state: RootState) => state.auth.user);
  const dispatch = useDispatch();
  const { authError, isAuthFetching } = useAuth();
  const { isSettingsFetching } = useFetchSettings(setTheme);
  const socket = useSocket();
  const router = useRouter();
  const { shouldShowPopup: shouldShowWhatsNewPopup } = useWhatsNewAccess();

  useEffect(() => {
    if (!socket) {
      return;
    }

    const createNotificationListener = (data) => {
      if (!authUser) return;
      if (data.user !== authUser._id) return;
      const exists = authUser.notifications.some((n) => n._id === data._id);
      if (!exists) {
        dispatch(addUserNotification(data));
      }
    };

    const deleteNotificationListener = (data) => {
      if (!authUser) return;
      if (data.user !== authUser._id) return;
      const exists = authUser.notifications.some((n) => n._id === data._id);
      if (exists) {
        dispatch(removeUserNotification(data._id));
      }
    };

    socket.on(Events.CREATE_NOTIFICATION_REQUEST, createNotificationListener);
    socket.on(Events.DELETE_NOTIFICATION_REQUEST, deleteNotificationListener);

    return () => {
      socket.off(Events.CREATE_NOTIFICATION_REQUEST, createNotificationListener);
      socket.off(Events.DELETE_NOTIFICATION_REQUEST, deleteNotificationListener);
    };
  }, [authUser, dispatch, socket]);

  if (isAuthFetching || isSettingsFetching) return <Loading top="md" />;
  if (authError) {
    const isDevelopment = !process.env.NODE_ENV || process.env.NODE_ENV === 'development';
    if (isDevelopment) {
      console.error(authError);
      return <div>{devErrorMessage}</div>;
    } else return <ErrorComponent errorMessage={JSON.stringify(authError)} />;
  }

  return (
    <>
      {children}

      <AnnouncementsModal
        enabled={!!authUser && shouldShowWhatsNewPopup()}
        isActive={!!authUser?.isActive}
        isBanned={!!authUser?.banned}
        pathname={router.pathname}
      />

      <GlobalStyle />

      {notification.message &&
        notification.message !== null &&
        notification.message !== undefined &&
        notification.message.toString().trim() !== '' && (
          <Alert autoClose={notification.autoClose} alignment={notification.alignment} type={notification.type}>
            {notification.message}
          </Alert>
        )}
    </>
  );
};

export default App;
